/**
 * Metadata Tab
 * Displays extracted PDF metadata
 */

import React from 'react';
import {
  Box,
  Typography,
  Grid,
  Paper,
  Alert,
  Divider
} from '@mui/material';
import { 
  Description as DocIcon,
  DateRange as DateIcon,
  Numbers as RevisionIcon,
  Title as TitleIcon
} from '@mui/icons-material';
import { PDFMetadata } from '../../types/bom.types';

interface MetadataTabProps {
  metadata: PDFMetadata | null;
}

const MetadataTab: React.FC<MetadataTabProps> = ({ metadata }) => {
  if (!metadata) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Alert severity="info">
          No metadata available. Extract from a page first.
        </Alert>
      </Box>
    );
  }

  const MetadataItem: React.FC<{ icon: React.ReactNode; label: string; value: any }> = ({ 
    icon, 
    label, 
    value 
  }) => (
    <Paper sx={{ p: 2 }} variant="outlined">
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        {icon}
        <Typography variant="caption" color="text.secondary" fontWeight={600}>
          {label}
        </Typography>
      </Box>
      <Typography variant="body1">
        {value || 'N/A'}
      </Typography>
    </Paper>
  );

  return (
    <Box>
      <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
        <DocIcon color="primary" />
        <Typography variant="h6">
          Document Metadata
        </Typography>
      </Box>

      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <MetadataItem
            icon={<TitleIcon color="primary" />}
            label="Drawing Number"
            value={metadata.drawing_number}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <MetadataItem
            icon={<RevisionIcon color="primary" />}
            label="Revision"
            value={metadata.revision}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <MetadataItem
            icon={<DateIcon color="primary" />}
            label="Date"
            value={metadata.date}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <MetadataItem
            icon={<TitleIcon color="primary" />}
            label="Title"
            value={metadata.title}
          />
        </Grid>
      </Grid>

      {(metadata.notes || metadata.description) && (
        <>
          <Divider sx={{ my: 3 }} />
          <Paper sx={{ p: 2 }} variant="outlined">
            <Typography variant="caption" color="text.secondary" fontWeight={600} gutterBottom>
              Additional Information
            </Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>
              {metadata.notes || metadata.description || 'N/A'}
            </Typography>
          </Paper>
        </>
      )}
    </Box>
  );
};

export default MetadataTab;

